import { useState, useEffect } from 'react';
import { SIGN_RU_TO_EN, SIGN_PREPOSITIONAL } from '@/data/signMap';
import { METRIKA_ID_NUM } from '@/lib/siteConfig';
import { BACKEND_URL } from '@/lib/backendUrl';
import { setArticleSequence } from '@/lib/articleReader';

// Только 10 планет, у которых есть статьи (узлы/Лилит/Хирон — без ссылок).
const PLANET_ARTICLE = new Set([
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars',
  'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto',
]);

// Статьи живут на астрофил.рф. Бэкенд отдаёт их полным адресом — так ссылка
// одна и та же на рф и на astrofeel.ru. На самом рф она становится
// относительной, как остальные ссылки сайта на себя.
export const RF_HOSTS = new Set(['астрофил.рф', 'xn--80aqjkjfhs.xn--p1ai', 'www.xn--80aqjkjfhs.xn--p1ai']);
export const RF_API_URL = 'https://xn--80aqjkjfhs.xn--p1ai/api';
const RF_URL_RE = /^https?:\/\/(?:www\.)?(?:астрофил\.рф|xn--80aqjkjfhs\.xn--p1ai)(\/[^\s]*)$/i;

export const isRfSite = () => typeof window !== 'undefined' && RF_HOSTS.has(window.location.hostname);

function localize(link) {
  if (!link || !link.url || !isRfSite()) return link;
  const m = RF_URL_RE.exec(link.url);
  return m ? { ...link, url: m[1] } : link;
}

function localizeAll(data) {
  if (!data) return null;
  const planets = {};
  Object.entries(data.planets || {}).forEach(([name, kinds]) => {
    planets[name] = {};
    Object.entries(kinds || {}).forEach(([kind, link]) => { planets[name][kind] = localize(link); });
  });
  const houses = {};
  Object.entries(data.houses || {}).forEach(([num, link]) => { houses[num] = localize(link); });
  return { planets, houses };
}

// Ссылка «Планета в N доме» несёт номер дома: у планет без своей статьи «в
// доме» она ведёт на «в знаке в домах», и читалка прокручивает статью к
// разделу этого дома.
function withHouses(data, bodies) {
  if (!data) return data;
  bodies.forEach((b) => {
    const kinds = data.planets[b.name];
    if (kinds && kinds.house && b.house) kinds.house = { ...kinds.house, house: Number(b.house) };
  });
  return data;
}

// Заголовки карты со ссылками по порядку расшифровки — для «Дальше: …» в
// читалке. Подписи — те же, что на карте: «Венера в Овне», «Венера в 12 доме».
function buildSequence(bodies, links) {
  const items = [];
  bodies.forEach((b) => {
    const kinds = links.planets[b.name];
    if (!kinds) return;
    const name = b.name_ru || b.name;
    if (kinds.sign && b.sign_ru) {
      items.push({ label: `${name} в ${SIGN_PREPOSITIONAL[b.sign_ru] || b.sign_ru}`, link: kinds.sign });
    }
    if (kinds.house && b.house) items.push({ label: `${name} в ${b.house} доме`, link: kinds.house });
  });
  return items;
}

/**
 * Резолвит ссылки на статьи для заголовков расшифровки. Адрес собирает и
 * проверяет бэкенд (справочник статей рф + запасная формула по базе).
 * Возвращает { planets: {name: {hub|sign|house|domah: {url,title,slug,source}}},
 * houses: {num: {...}} } или null.
 *
 * Адрес бэкенда — общий BACKEND_URL: пустая переменная значит «тот же домен».
 * Раньше хук читал REACT_APP_BACKEND_URL сам и при пустой молча не делал
 * запрос — на рф, где переменная не задана, ссылки пропали целиком.
 */
export function useArticleLinks(chartData) {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    if (!chartData) return;

    const bodies = [...(chartData.planets || []), ...(chartData.fictivePoints || [])]
      .filter((b) => PLANET_ARTICLE.has(b.name));
    const planets = bodies.map((b) => ({
      planet: b.name,
      sign: SIGN_RU_TO_EN[b.sign_ru] || null,
      house: b.house || null,
    }));
    const houses = (chartData.houses || []).map((h, i) => h.house || (i + 1));
    if (!planets.length && !houses.length) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/cms/natal/article-links`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planets, houses }),
        });
        if (!res.ok || cancelled) return;
        const data = withHouses(localizeAll(await res.json()), bodies);
        if (cancelled) return;
        setLinks(data);
        if (data) setArticleSequence(buildSequence(bodies, data));
      } catch {
        /* тихо — просто не будет ссылок */
      }
    })();
    return () => { cancelled = true; };
  }, [chartData]);
  return links;
}

// Цель Метрики на клик по ссылке-статье (тот же счётчик, что в остальных целях).
export function trackArticleLinkClick() {
  try {
    if (typeof window !== 'undefined' && typeof window.ym === 'function') {
      if (METRIKA_ID_NUM) window.ym(METRIKA_ID_NUM, 'reachGoal', 'article_link');
    }
  } catch {
    /* no-op */
  }
}
